import {differenceInSeconds, subHours} from 'date-fns';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    addDurationToDate,
    autoinject,
    bind,
    componentReaction,
    displayUtcToZonedTimeFactory,
    Duration,
    inject,
    LiveChart,
    Loader,
    subDurationFromDate,
    TasksStore,
    Mutex,
} from 'Mri.Web.Common';
import { stringify } from 'qs';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { AppStore } from '../../../Core/AppStore';
import { DisplaysChartsStore } from './DisplaysChartsStore';
import { DisplaysStore } from '../../DisplaysStore';
import { LiveChartsTopBar } from './LiveChartsTopBar';
import { debounce } from 'lodash';
import { BsExclamationTriangle } from '@react-icons/all-files/bs/BsExclamationTriangle';

export interface IDisplayLiveChartsViewParams {
    serialNumber: string;
}
export interface IDisplayLiveChartsViewProps extends RouteComponentProps<IDisplayLiveChartsViewParams> {}

@autoinject
@observer
export class DisplayLiveChartsView extends React.Component<IDisplayLiveChartsViewProps> {
    public constructor(props: IDisplayLiveChartsViewProps) {
        super(props);
        const trackedValues = computed(() => [
            this.selectedChart,
            this.appStore.storage.liveChartSettings.liveChartTimeWindow,
            this.startTime,
            this.maxValue,
            this.appStore.storage.liveChartSettings.triggerSecsPerSampleUpdate,
        ]);

        componentReaction(this, () => trackedValues.get(), this.debouncedLoadTrackData);
    }

    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            await this.displaysStore.loadAsync(this.props.match.params.serialNumber);
        } catch (e: any) {
            this.appStore.setError(e);
            return;
        }

        try {
            await this.displaysChartsStore.loadChartConfigAsync(this.props.match.params.serialNumber);

            if (this.chartConfig) {
                this.startTime = this.maxValue;

                this.loading = false;
                await this.loadTrackData();
            }
        } catch (e: any) {
            this.tasksStore.toastErrorTask('Load Chart Config', e);
            this.error = e;
        } finally {
            this.loading = false;
        }

        if (this.chartConfig) {
            let refreshRate = this.appStore.storage.liveChartSettings.secsPerSample > 60 ?  this.appStore.storage.liveChartSettings.secsPerSample * 2000 : 120000;

            this.refreshTimer = window.setInterval(this.refresh, refreshRate);
        }
    }

    public async componentWillUnmount() {
        if (this.refreshTimer) window.clearInterval(this.refreshTimer);
        if (this.abortController) this.abortController.abort();
        this.appStore.showHeader();
    }

    public render() {
        const loading = this.loading && !this.liveRefreshing;
        return (
            <div className="container-fluid p-3 h-100">
                {this.chartConfig && this.selectedChart && this.endTime && this.startTime && (
                    <LiveChartsTopBar
                        onChartNameClick={this.onChartNameClick}
                        onTimeWindowChange={this.onTimeWindowChange}
                        onStartTimeChange={this.onStartTimeChange}
                        singleChart
                        startTime={this.startTime}
                        endTime={this.endTime}
                        sliderMin={this.chartConfig.firstAvailableSampleTime.valueOf()}
                        serialNumber={this.props.match.params.serialNumber}
                        hasMoreDataThanWindow={this.hasMoreDataThanWindow}
                        liveRefresh={this.liveRefresh}
                        maxValue={this.maxValue}
                        timeWindow={this.timeWindow}
                        chartNames={this.chartNames}
                    />
                )}

                <div className="row h-100">
                    <div className="col-12 h-100">
                        {!loading &&
                            this.selectedChart &&
                            this.trackDatas &&
                            !this.error &&
                            this.startTime &&
                            this.endTime &&
                            this.chartConfig && (
                            <LiveChart
                                chartConfig={this.selectedChart}
                                displaySerialNumber={this.props.match.params.serialNumber}
                                trackDatas={this.trackDatas}
                                startTime={this.startTime}
                                endTime={this.endTime}
                                firstAvailableSampleTime={this.chartConfig.firstAvailableSampleTime}
                                mobile={this.appStore.isMobile}
                                expanded={this.appStore.hiddenHeader}
                                onExpand={this.appStore.hideHeader}
                                onCollapse={this.appStore.showHeader}
                            />
                        )}

                        {!loading && this.error && (
                            <div className="bg-gray-darker rounded h-100 live-chart-container d-flex flex-column">
                                <div className="flex-items-center justify-content-center h-100 w-100">
                                    <div className="d-inline-block">
                                        <BsExclamationTriangle className=" text-danger" /> &nbsp;
                                        {typeof this.error == 'object'
                                            ? this.error.message ?? this.error.Message
                                            : this.error}
                                    </div>
                                </div>
                            </div>
                        )}

                        {loading && (
                            <div className="bg-gray-darker rounded h-100 live-chart-container d-flex flex-column">
                                <div className="flex-items-center h-100 w-100">
                                    <Loader loading={loading} message="Loading Data" showSpinner center />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    private onChartNameClick(chartName: string) {
        const search = stringify({
            chartName,
        });

        this.props.history.push({
            pathname: this.props.location.pathname,
            search: '?' + search,
        });
    }

    @bind
    @action
    private onTimeWindowChange() {
        if (this.chartConfig) {
            this.startTime = this.maxValue;
            
            if (this.refreshTimer) window.clearInterval(this.refreshTimer);
            let refreshRate = this.appStore.storage.liveChartSettings.secsPerSample > 60 ?  this.appStore.storage.liveChartSettings.secsPerSample * 2000 : 120000;

            this.refreshTimer = window.setInterval(this.refresh, refreshRate);
        }
    }

    @bind
    @action
    private onStartTimeChange(startTime: Date) {
        this.startTime = startTime;
    }

    @bind
    @action
    private async loadTrackData() {
        if (!this.selectedChart || !this.startTime || !this.endTime || this.loading) return;

        if (this.abortController) {
            this.abortController.abort();
            this.abortController = undefined;
        }

        await this.loadLock.waitAsync();
        this.loading = true;
        this.abortController = new AbortController();
        try {
            for (const trackInfo of this.selectedChart.trackInfo) {
                await this.displaysChartsStore.loadTrackDataAsync(
                    this.props.match.params.serialNumber,
                    trackInfo.varNum,
                    this.startTime!,
                    this.endTime!,
                    this.appStore.storage.liveChartSettings.secsPerSample,
                    this.liveRefreshing,
                    { signal: this.abortController.signal }
                );
            }

            this.loading = false;
            this.liveRefreshing = false;
        } catch (e: any) {
            if (e instanceof DOMException) {
                console.info('Cancelled');
            } else {
                this.tasksStore.toastErrorTask('Load Track Data', e);
                this.error = e;

                this.loading = false;
                this.liveRefreshing = false;
            }
        } finally {
            this.abortController = undefined;
            this.loadLock.release();
        }
    }

    @bind
    @action
    private async refresh() {
        if (!this.liveRefresh || !this.chartConfig || this.loading || this.liveRefreshing) return;

        console.debug('Refresh Charts');
        this.liveRefreshing = true;
        this.now = new Date();
        this.startTime = this.maxValue;
    }

    @computed
    private get hasMoreDataThanWindow() {
        if (!this.chartConfig) return false;

        return this.chartConfig.firstAvailableSampleTime < this.maxValue;
    }

    @computed
    private get endTime() {
        if (!this.startTime) return;
        
        return this.hasMoreDataThanWindow ? addDurationToDate(new Date(this.startTime), this.timeWindow) : this.now;
    }

    @computed
    private get chartConfig() {
        return this.displaysChartsStore.displaysCharts.get(this.props.match.params.serialNumber);
    }

    @computed
    private get trackDatas() {
        return this.displaysChartsStore.displayTrackDatas.get(this.props.match.params.serialNumber);
    }

    @computed
    private get selectedChart() {
        if (this.chartConfig?.chartConfigs) {
            const chartConfig = this.chartConfig.chartConfigs.find(
                c => c.chartName == this.appStore.storage.liveChartSettings.liveChartSelectedChartName
            );
            if (chartConfig) {
                return chartConfig;
            } else if (!!this.chartConfig.chartConfigs.length) {
                return this.chartConfig.chartConfigs[0];
            }
        }
    }

    @computed
    private get timeWindow() {
        return new Duration((this.appStore.storage.liveChartSettings.liveChartTimeWindow ?? 2) * 60 * 60 * 1000);
    }

    @computed
    private get maxValue() {
        return subDurationFromDate(this.now, this.timeWindow);
    }

    @computed
    private get liveRefresh() {
        if (!this.startTime) return false;
        if (this.error) return false;
        return differenceInSeconds(this.maxValue, this.startTime) < 60;
    }

    @computed
    private get chartNames() {
        return this.chartConfig?.chartConfigs.map(c => c.chartName) ?? [];
    }

    private abortController?: AbortController;
    private readonly loadLock = new Mutex();
    private debouncedLoadTrackData = debounce(this.loadTrackData, 50);
    @observable
    private loading = false;
    @observable
    private liveRefreshing = false;
    @observable
    private error: any;
    @observable
    private startTime?: Date;
    @observable
    private now = new Date();
    private refreshTimer?: number;
    @inject
    private appStore!: AppStore;
    @inject
    private tasksStore!: TasksStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private displaysChartsStore!: DisplaysChartsStore;
}
